import {
  Avatar,
  Box,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { DarkModeOutlined, LightModeOutlined } from "@mui/icons-material";

import { useContext } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/Index";
import { ColorModeContext, tokens } from "../../../theme";
import GlobalSearchBar from "../../../components/common/GlobalSearchBar";
import { UserIcon } from "./icons/UserIcon";
import { User } from "../../../store/slice/AuthSlice";
import PendingSiteVisitWidget from "./PendingSiteVisitWidget";
import CreateBookingForm from "./CreateBookingForm";
import MemoryBookSummary from "./MemoryBookSummary";

const AppHomeContent = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const colorMode = useContext(ColorModeContext);

  const user = useSelector(
    (state: RootState) => state.auth.user,
  ) as User | null;

  const users = useSelector((state: RootState) => state.users.users);
  const userDetails = users.find((u) => u.id === user?.id);

  const getCurrentDate = () => {
    const days = [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ];
    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    const now = new Date();
    return `${days[now.getDay()]}, ${months[now.getMonth()]} ${now.getDate()}, ${now.getFullYear()}`;
  };

  return (
    <Box
      sx={{
        width: { xs: "95%", md: "85%" },
        mx: "auto",
        minHeight: "100vh",
        backgroundColor: "transparent",
        py: { xs: 4, md: 0 },
        px: { xs: 2, md: 4 },
        transform: { xs: "scale(0.9)", md: "scale(0.88)" },
        transformOrigin: "top center",
        transition: "transform 0.3s ease",
        position: "relative",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: { xs: 20, xl: 40 },
          right: { xs: 0, md: 0, lg: -170 },
          display: { xs: "none", lg: "block" },
          zIndex: 2,
        }}
      >
        <PendingSiteVisitWidget />
      </Box>
      {/* Welcome Section */}
      <Box
        sx={{
          textAlign: "center",
          mb: { xs: 4, md: 4 },
          mt: { xs: 0, md: 2 },
          maxWidth: "1200px",
          mx: "auto",
        }}
      >
        <Box
          sx={{ display: "inline-flex", alignItems: "center", gap: 2, mb: 2 }}
        >
          <Typography
            variant="h1"
            sx={{
              fontWeight: "bold",
              color: colors.primary[700],
              fontSize: { xs: "3rem", md: "4rem" },
            }}
          >
            Hi
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              borderRadius: "50px",
              pr: 3,
              pl: 1,
              py: 1,
              border: `1px solid ${colors.primary[700]}`,
            }}
          >
            <Avatar
              src={userDetails?.profile_picture_url || undefined}
              sx={{
                width: 44,
                height: 44,
                backgroundColor: colors.primary[400],
              }}
            >
              {!userDetails?.profile_picture_url && (
                <UserIcon sx={{ color: "white" }} />
              )}
            </Avatar>
            <Box sx={{ textAlign: "left" }}>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 600,
                  color: colors.primary[100],
                  fontSize: "0.95rem",
                  lineHeight: 1.2,
                }}
              >
                {user?.first_name} {user?.last_name}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: colors.primary[700],
                  fontSize: "0.75rem",
                  textTransform: "capitalize",
                }}
              >
                {userDetails?.role || "User"}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Typography
          variant="body2"
          sx={{ color: colors.primary[700], fontSize: "0.9rem" }}
        >
          {`Today's date is ${getCurrentDate()}`}
        </Typography>
      </Box>

      {/* Global Search Bar */}
      <Box
        sx={{
          maxWidth: "620px",
          mx: "auto",
          mb: 4,
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <Box sx={{ flex: 1, position: "relative" }}>
          <GlobalSearchBar placeholder="Search bookings, streams, venues, contacts…" />
        </Box>
        <Tooltip
          title={theme.palette.mode === "dark" ? "Light Mode" : "Dark Mode"}
          arrow
        >
          <IconButton
            onClick={colorMode.toggleColorMode}
            sx={{
              color: colors.primary[700],
              border: `1px solid ${colors.primary[300]}`,
              borderRadius: "12px",
              width: 44,
              height: 44,
            }}
          >
            {theme.palette.mode === "dark" ? (
              <LightModeOutlined />
            ) : (
              <DarkModeOutlined />
            )}
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ maxWidth: "1000px", mx: "auto", mb: 2 }}>
        <CreateBookingForm
          buttonColor={
            !userDetails?.profile_picture_url ? colors.primary[400] : undefined
          }
        />
      </Box>
      <MemoryBookSummary />
    </Box>
  );
};

export default AppHomeContent;
